import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button, IconButton, Tooltip } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ContextNoteDialog from '../components/dialogs/ContextNoteDialog';
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";


const fadeIn     = { hidden:{opacity:0}, visible:{opacity:1,transition:{duration:0.5}} };
const leftEntry  = { hidden:{x:-40,opacity:0}, visible:{x:0,opacity:1,transition:{duration:0.5}} };
const rightEntry = { hidden:{x:40,opacity:0},  visible:{x:0,opacity:1,transition:{duration:0.5}} };
const lineEntry  = {
  hidden:{opacity:0,y:6},
  visible:(i)=>({opacity:1,y:0,transition:{duration:0.2,delay:i*0.025}}),
};

const PALETTES = [
  {bubble:"rgba(128,24,231,0.13)", border:"rgba(128,24,231,0.5)",  label:"#a855f7", glow:"#8018e720"},
  {bubble:"rgba(16,185,129,0.11)", border:"rgba(16,185,129,0.45)", label:"#10b981", glow:"#10b98120"},
  {bubble:"rgba(59,130,246,0.12)", border:"rgba(59,130,246,0.45)", label:"#60a5fa", glow:"#3b82f620"},
  {bubble:"rgba(249,115,22,0.12)", border:"rgba(249,115,22,0.45)", label:"#fb923c", glow:"#f9731620"},
  {bubble:"rgba(236,72,153,0.12)", border:"rgba(236,72,153,0.45)", label:"#f472b6", glow:"#ec489920"},
  {bubble:"rgba(20,184,166,0.12)", border:"rgba(20,184,166,0.45)", label:"#2dd4bf", glow:"#14b8a620"},
];

const BRACKET_COLORS = ["#e879f9","#38bdf8","#34d399","#fbbf24","#f87171","#a78bfa"];

const SAMPLE = [
  {
    id: 1,
    prompt: {
      domain_tags: ["SARCASTIC","social","flatmates"],
      scenario: {
        scene: [
          { type:"narration",  text:"Nadia and her flatmate Corso are having people over. Nadia's boyfriend Rem arrived an hour ago. Corso and Rem have met twice before and do not particularly like each other. The other guests have stepped outside." },
          { type:"dialogue", speaker:"Corso", text:"So are you still doing the photography thing?" },
          { type:"dialogue", speaker:"Rem",   text:"Yeah. Just finished a commercial shoot actually." },
          { type:"dialogue", speaker:"Corso", text:"Commercial. Nice. That pays?" },
          { type:"dialogue", speaker:"Rem",   text:"Better than it used to." },
          { type:"dialogue", speaker:"Corso", text:"I always figured you'd go more the artistic route. Like galleries, that kind of thing." },
          { type:"dialogue", speaker:"Rem",   text:"I do both." },
          { type:"dialogue", speaker:"Corso", text:"Sure, sure. It's just — commercial work, you know. You're always making someone else's vision." },
          { type:"dialogue", speaker:"Rem",   text:"That's one way to look at it." },
          { type:"dialogue", speaker:"Corso", text:"No, I get it. Bills are bills. Nothing wrong with it." },
          { type:"action",   speaker:"Nadia", text:"Comes back in from the hallway and picks up her drink." },
          { type:"dialogue", speaker:"Corso", text:"We were just talking about Rem's work. Really cool stuff." },
          { type:"action",   speaker:"Rem",   text:"Nods once and looks toward the window." },
        ],
      },
    },
    keys: [
      { qid:"1.1", answer:"FALSE", question:"Is Corso being SARCASTIC when he says 'Really cool stuff' to Nadia?" },
      { qid:"1.2", answer:"FALSE", question:"Is Rem OFFENDED during this exchange?" },
      { qid:"1.3", answer:"FALSE", question:"Is Corso offering PERFORMATIVE_VALIDATION?" },
    ],
  },
  {
    id: 2,
    prompt: {
      domain_tags: ["SARCASTIC","workplace","public_praise"],
      scenario: {
        scene: [
          { type:"narration",  text:"A team of six has just finished a product demo for internal stakeholders. The demo ran twenty minutes over time and the live integration failed halfway through. Afterwards, people are gathering their things in the meeting room." },
          { type:"dialogue", speaker:"Gregor", text:"Well. That went smoothly." },
          { type:"action",   speaker:"Yuna",   text:"Closes her laptop without looking up." },
          { type:"dialogue", speaker:"Bram",   text:"The fallback slides helped at the end at least." },
          { type:"dialogue", speaker:"Gregor", text:"Sure. Nothing like a live failure to really sell confidence." },
          { type:"dialogue", speaker:"Bram",   text:"The stakeholders seemed okay with it." },
          { type:"dialogue", speaker:"Gregor", text:"They were very gracious about it, yes." },
          { type:"action",   speaker:"Yuna",   text:"Picks up her bag and moves toward the door." },
          { type:"dialogue", speaker:"Bram",   text:"We can debrief tomorrow and figure out what happened with the integration." },
          { type:"dialogue", speaker:"Gregor", text:"Looking forward to it." },
          { type:"action",   speaker:"Bram",   text:"Nods and follows Yuna out." },
          { type:"action",   speaker:"Gregor", text:"Stays behind and stacks the printed slide decks into a neat pile." },
        ],
      },
    },
    keys: [
      { qid:"2.1", answer:"TRUE",  question:"Is Gregor being SARCASTIC when he says 'That went smoothly'?" },
      { qid:"2.2", answer:"TRUE",  question:"Is Gregor being SARCASTIC when he says 'Looking forward to it'?" },
      { qid:"2.3", answer:"FALSE", question:"Is Bram giving Gregor PERFORMATIVE_VALIDATION when he says the stakeholders seemed okay?" },
    ],
  },
  {
    id: 3,
    prompt: {
      domain_tags: ["FALSE_ASSUMPTION","social","long_term_friends"],
      scenario: {
        scene: [
          { type:"narration", text:"Daria and Søren have been close friends since university. They now live in different cities. Søren recently ended a long relationship. Daria does not know this — the last time they spoke, things between Søren and his partner seemed fine." },
          { type:"message", speaker:"Daria", text:"Hey! I'm going to be in your city the weekend of the 14th. Would love to finally meet Klara properly — we keep missing each other." },
          { type:"message", speaker:"Søren", text:"That weekend works. Come over Saturday evening." },
          { type:"message", speaker:"Daria", text:"Amazing. I'll bring something nice. Should I get wine or does Klara prefer something else? I can never remember." },
          { type:"message", speaker:"Søren", text:"Wine is fine. Just bring whatever you like." },
          { type:"message", speaker:"Daria", text:"Perfect. I'm excited — it'll be good to all catch up together." },
          { type:"message", speaker:"Søren", text:"Yeah. See you then." },
        ],
      },
    },
    keys: [
      { qid:"3.1", answer:"FALSE", question:"Is Søren giving Daria a SOFT_REJECTION?" },
      { qid:"3.2", answer:"TRUE",  question:"Is Søren AWARE_OTHER_IS_WRONG in this exchange?" },
    ],
  },
  {
    id: 4,
    prompt: {
      domain_tags: ["REGISTER_MISMATCH","family","formal_occasion"],
      scenario: {
        scene: [
          { type:"narration",  text:"A family is gathered at a restaurant for a grandmother's eightieth birthday dinner. The table seats twelve. The grandmother is at the head. Most conversation has been measured and warm. Lucie, who is in her late twenties, has just arrived late and taken the last open seat." },
          { type:"dialogue", speaker:"Lucie",       text:"Sorry, sorry — the tram was a nightmare. Happy birthday, Gran." },
          { type:"action",   speaker:"Grandmother", text:"Reaches across and pats Lucie's hand." },
          { type:"dialogue", speaker:"Lucie",       text:"Okay so what did I miss? Anyone get engaged yet or is that still me being optimistic?" },
          { type:"action",   speaker:"Aunt Vera",   text:"Sets down her glass." },
          { type:"dialogue", speaker:"Lucie",       text:"I'm kidding. Mostly. Anyway — Gran, eighty is insane, you look incredible. What's your secret, red wine every night?" },
          { type:"dialogue", speaker:"Grandmother", text:"Olive oil and minding my own business." },
          { type:"action",   speaker:"Lucie",       text:"Laughs and reaches for the bread basket." },
          { type:"dialogue", speaker:"Lucie",       text:"I heard Uncle Pav is thinking of selling the house — is that actually happening or just a rumor?" },
          { type:"action",   speaker:"Uncle Pav",   text:"Looks down at his plate." },
          { type:"dialogue", speaker:"Lucie",       text:"What? I figured everyone knew." },
          { type:"action",   speaker:"Aunt Vera",   text:"Refills her water glass slowly." },
        ],
      },
    },
    keys: [
      { qid:"4.1", answer:"FALSE", question:"Is Lucie showing REGISTER_MISMATCH in this scene?" },
      { qid:"4.2", answer:"TRUE",  question:"Is Lucie committing a NORM_VIOLATION when she asks about Uncle Pav's house?" },
      { qid:"4.3", answer:"FALSE", question:"Is the grandmother being SARCASTIC in her reply about olive oil?" },
    ],
  },
];

// ── JSON validator ─────────────────────────────────────────────────────────────
function validateJson(text) {
  if (!text.trim()) return { valid:true, error:null };
  try { JSON.parse(text); return { valid:true, error:null }; }
  catch(e) {
    const msg = e.message;
    const posMatch = msg.match(/position (\d+)/i);
    let lineNum = null;
    if (posMatch) lineNum = text.slice(0, parseInt(posMatch[1])).split("\n").length;

    const opens = [];
    let inStr = false, escape = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (escape) { escape = false; continue; }
      if (ch === '\\' && inStr) { escape = true; continue; }
      if (ch === '"') { inStr = !inStr; continue; }
      if (inStr) continue;
      if (ch === '{' || ch === '[') opens.push({ ch, line: text.slice(0,i).split("\n").length });
      if (ch === '}' || ch === ']') {
        const exp = ch === '}' ? '{' : '[';
        if (opens.length && opens[opens.length-1].ch === exp) opens.pop();
        else {
          const ln = text.slice(0,i).split("\n").length;
          return { valid:false, error:`Line ${ln}: unexpected '${ch}' — no matching opening bracket` };
        }
      }
    }
    if (opens.length) {
      const last = opens[opens.length-1];
      return { valid:false, error:`Line ${last.line}: '${last.ch}' was never closed — missing '${last.ch==='{'?'}':']'}'` };
    }

    let friendly = msg;
    if (/trailing comma/i.test(msg) || /unexpected token '?,/.test(msg)) friendly = "Trailing comma — remove the comma after the last item";
    else if (/unterminated string/i.test(msg)) friendly = "Unterminated string — missing closing quote";
    else if (/unexpected token '?}/.test(msg)) friendly = "Unexpected } — extra closing brace";
    else if (/unexpected token '?]/.test(msg)) friendly = "Unexpected ] — extra closing bracket";
    else if (/expected.*':'/.test(msg)) friendly = "Missing colon `:` between key and value";
    else if (/unexpected token/i.test(msg)) friendly = "Unexpected token — check quotes and commas near here";

    return { valid:false, error: lineNum ? `Line ${lineNum}: ${friendly}` : friendly };
  }
}

// ── JSON colorizer ─────────────────────────────────────────────────────────────
function colorizeJson(text) {
  const lines = text.split("\n");
  let depth = 0;
  return lines.map((line, lineIdx) => {
    const tokens = [];
    let i = 0;
    const push = (t, c) => tokens.push({ text:t, color:c });
    while (i < line.length) {
      const ch = line[i];
      if (ch === '{' || ch === '[') { push(ch, BRACKET_COLORS[depth % BRACKET_COLORS.length]); depth++; i++; }
      else if (ch === '}' || ch === ']') { depth = Math.max(0, depth-1); push(ch, BRACKET_COLORS[depth % BRACKET_COLORS.length]); i++; }
      else if (ch === '"') {
        let str = '"'; i++;
        while (i < line.length) {
          if (line[i] === '\\') { str += line[i] + (line[i+1]||''); i += 2; continue; }
          if (line[i] === '"') { str += '"'; i++; break; }
          str += line[i++];
        }
        push(str, "#86efac");
      }
      else if (/\d/.test(ch) || (ch === '-' && /\d/.test(line[i+1]||''))) {
        let num = ''; while (i < line.length && /[\d.\-eE+]/.test(line[i])) num += line[i++];
        push(num, "#fca5a5");
      }
      else if (line.slice(i,i+4) === 'true')  { push('true',  "#fbbf24"); i += 4; }
      else if (line.slice(i,i+5) === 'false') { push('false', "#fbbf24"); i += 5; }
      else if (line.slice(i,i+4) === 'null')  { push('null',  "#94a3b8"); i += 4; }
      else if (ch === ':') { push(':', '#94a3b8'); i++; }
      else if (ch === ',') { push(',', '#64748b'); i++; }
      else { push(ch, 'rgba(255,255,255,0.45)'); i++; }
    }
    return { tokens, lineNum: lineIdx+1 };
  });
}

// ── helpers ────────────────────────────────────────────────────────────────────
function buildPaletteMap(scene = []) {
  const map = {}; let idx = 0;
  scene.forEach(l => { const sp = l.speaker||l.actor; if (sp && !map[sp]) map[sp] = PALETTES[idx++ % PALETTES.length]; });
  return map;
}

// ── scene components ───────────────────────────────────────────────────────────
const ActorLegend = React.memo(({ paletteMap }) => {
  const entries = Object.entries(paletteMap);
  if (!entries.length) return null;
  return (
    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
      {entries.map(([name, p]) => (
        <span key={name} style={{
          fontSize:12, fontFamily:"'Barlow',sans-serif", fontWeight:600,
          letterSpacing:"0.08em", textTransform:"uppercase",
          padding:"4px 13px", border:`1px solid ${p.border}`, background:p.bubble, color:p.label,
        }}>{name}</span>
      ))}
    </div>
  );
});

const SceneLine = React.memo(({ line, paletteMap, index, onTextClick }) => {
  const speaker = line.speaker || line.actor;
  const type    = line.type;

  const makeClickable = (text) => ({
    onClick: (e) => {
      if (!onTextClick) return;
      const rect  = e.currentTarget.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      onTextClick(text, Math.round(ratio * text.length));
    },
    style: { cursor:"text" },
  });

  if (type === "narration") return (
    <motion.div custom={index} variants={lineEntry} initial="hidden" animate="visible"
      {...makeClickable(line.text)}
      style={{
        fontFamily:"'Barlow',sans-serif", fontWeight:300, fontSize:15,
        color:"rgba(255,255,255,0.6)", fontStyle:"italic",
        borderLeft:"2px solid rgba(255,255,255,0.18)", paddingLeft:12,
        margin:"6px 0", lineHeight:1.65, cursor:"text",
      }}>{line.text}</motion.div>
  );

  if (type === "action") {
    const p = speaker && paletteMap[speaker];
    return (
      <motion.div custom={index} variants={lineEntry} initial="hidden" animate="visible" style={{
        fontFamily:"'Barlow',sans-serif", fontSize:14,
        color: p ? p.label : "rgba(255,255,255,0.65)",
        fontStyle:"italic", textAlign:"left", margin:"5px 0", paddingLeft:2,
      }}>
        {speaker && <span style={{ fontWeight:700, marginRight:6 }}>{speaker}</span>}
        <span {...makeClickable(line.text)} style={{ color:"rgba(255,255,255,0.78)", cursor:"text" }}>
          ({line.text})
        </span>
      </motion.div>
    );
  }

  const p = (speaker && paletteMap[speaker]) || PALETTES[0];
  return (
    <motion.div custom={index} variants={lineEntry} initial="hidden" animate="visible" style={{ margin:"4px 0" }}>
      <div style={{
        fontSize:12, fontFamily:"'Barlow',sans-serif", fontWeight:600,
        letterSpacing:"0.09em", textTransform:"uppercase", color:p.label, marginBottom:3, paddingLeft:2,
      }}>{speaker}</div>
      <div {...makeClickable(line.text)} style={{
        background:p.bubble, border:`1px solid ${p.border}`, padding:"10px 15px",
        fontSize:15, fontFamily:"'Barlow',sans-serif", fontWeight:300,
        color:"rgba(255,255,255,0.92)", lineHeight:1.65, maxWidth:"88%",
        boxShadow:`0 0 14px ${p.glow}`, cursor:"text",
      }}>{line.text}</div>
    </motion.div>
  );
});

const SceneBlock = React.memo(({ entry, offset, onTextClick }) => {
  const scene      = entry?.prompt?.scenario?.scene || entry?.prompt?.scene || [];
  const keys       = entry?.keys || [];
  const tags       = entry?.prompt?.domain_tags || [];
  const paletteMap = useMemo(() => buildPaletteMap(scene), [scene]);

  return (
    <div style={{ marginBottom:32 }}>
      {tags.length > 0 && (
        <div style={{ display:"flex", gap:6, marginBottom:8, flexWrap:"wrap" }}>
          {tags.map(tag => (
            <span key={tag} style={{
              fontSize:11, padding:"3px 10px",
              background:"rgba(59,130,246,0.1)", border:"1px solid rgba(59,130,246,0.3)",
              color:"#60a5fa", letterSpacing:"0.06em", fontWeight:600, fontFamily:"'Barlow',sans-serif",
            }}>{tag}</span>
          ))}
        </div>
      )}
      <ActorLegend paletteMap={paletteMap} />
      <div style={{ marginTop:10, display:"flex", flexDirection:"column", gap:4 }}>
        {scene.map((line, i) => (
          <SceneLine key={i} line={line} paletteMap={paletteMap} index={offset+i} onTextClick={onTextClick} />
        ))}
      </div>
      {keys.length > 0 && (
        <div style={{ marginTop:14, borderTop:"1px solid rgba(255,255,255,0.07)", paddingTop:10 }}>
          <div style={{ fontSize:10, letterSpacing:"0.09em", textTransform:"uppercase", color:"rgba(255,255,255,0.25)", fontFamily:"'Barlow',sans-serif", fontWeight:500, marginBottom:6 }}>
            Answer keys
          </div>
          {keys.map(q => {
            const ok = q.answer === "TRUE";
            return (
              <div key={q.qid} style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                <span style={{
                  flexShrink:0, fontSize:11, fontWeight:700, fontFamily:"'Barlow',sans-serif",
                  letterSpacing:"0.06em", padding:"3px 10px", marginTop:1,
                  background: ok ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.12)",
                  border: `1px solid ${ok ? "rgba(16,185,129,0.4)" : "rgba(239,68,68,0.35)"}`,
                  color: ok ? "#10b981" : "#ef4444",
                }}>{q.answer}</span>
                <span style={{ fontSize:13, color:"rgba(255,255,255,0.65)", fontFamily:"'Barlow',sans-serif", lineHeight:1.55 }}>
                  <span style={{ color:"rgba(255,255,255,0.3)", marginRight:8 }}>{q.qid}</span>
                  {q.question || ""}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

// ── Custom gradient scrollbars ─────────────────────────────────────────────────
const GRAD  = "linear-gradient(to bottom, #8018e7, #10b981, #3b82f6)";
const BAR_T = 5;
const BAR_V = 3.5;

const GradientBar = ({ scrollEl, axis }) => {
  const [thumb,   setThumb]   = useState({ pos:0, size:0 });
  const [visible, setVisible] = useState(false);
  const [showH,   setShowH]   = useState(false);
  const fadeTimer             = useRef(null);

  useEffect(() => {
    const el = scrollEl.current;
    if (!el) return;
    const update = () => {
      if (axis === "v") {
        if (el.scrollHeight <= el.clientHeight) { setVisible(false); return; }
        const ratio = el.clientHeight / el.scrollHeight;
        const pos   = (el.scrollTop / (el.scrollHeight - el.clientHeight)) * (el.clientHeight * (1 - ratio));
        setThumb({ pos, size: el.clientHeight * ratio });
        setVisible(true);
        clearTimeout(fadeTimer.current);
        fadeTimer.current = setTimeout(() => setVisible(false), 5000);
      } else {
        if (el.scrollWidth <= el.clientWidth) { setShowH(false); return; }
        const ratio = el.clientWidth / el.scrollWidth;
        const pos   = (el.scrollLeft / (el.scrollWidth - el.clientWidth)) * (el.clientWidth * (1 - ratio));
        setThumb({ pos, size: el.clientWidth * ratio });
        setShowH(el.scrollLeft > 4);
      }
    };
    el.addEventListener("scroll", update);
    update();
    return () => { el.removeEventListener("scroll", update); clearTimeout(fadeTimer.current); };
  }, [scrollEl, axis]);

  if (axis === "h") return (
    <div style={{
      position:"absolute", bottom:0, left:0, right:0, height:BAR_T,
      pointerEvents:"none", zIndex:20,
      opacity: showH ? 0.6 : 0, transition:"opacity 0.4s",
    }}>
      <div style={{
        position:"absolute", bottom:0, height:BAR_T,
        left:thumb.pos, width:thumb.size,
        background:GRAD, borderRadius:BAR_T,
      }}/>
    </div>
  );

  return (
    <div style={{
      position:"absolute", top:0, right:0, bottom:0, width:BAR_V,
      pointerEvents:"none", zIndex:20,
      opacity: visible ? 0.6 : 0, transition:"opacity 0.6s",
    }}>
      <div style={{
        position:"absolute", right:0, width:BAR_V,
        top:thumb.pos, height:thumb.size,
        background:GRAD, borderRadius:BAR_T,
      }}/>
    </div>
  );
};

// ── JSON viewer ────────────────────────────────────────────────────────────────
const JsonViewer = ({ text, readOnly, onChange, fontSize, taRef }) => {
  const lineH     = fontSize * 1.7;
  const gutterW   = 38;
  const colorized = useMemo(() => colorizeJson(text), [text]);
  const scrollRef = useRef(null);
  const gutterRef = useRef(null);
  const mirrorRef = useRef(null);

  const syncScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (gutterRef.current) gutterRef.current.scrollTop = el.scrollTop;
    if (mirrorRef.current) {
      mirrorRef.current.scrollTop  = el.scrollTop;
      mirrorRef.current.scrollLeft = el.scrollLeft;
    }
  };

  const gutterStyle = {
    width:gutterW, flexShrink:0, overflowY:"hidden",
    padding:`${lineH*0.7}px 0`, background:"rgba(0,0,0,0.15)",
    borderRight:"1px solid rgba(255,255,255,0.05)", pointerEvents:"none",
  };
  const lineNumStyle = {
    height:lineH, lineHeight:`${lineH}px`,
    fontSize:11, fontFamily:"monospace",
    color:"rgba(255,255,255,0.2)", textAlign:"right",
    paddingRight:8, userSelect:"none",
  };

  const Lines = () => colorized.map(({ tokens, lineNum }) => (
    <div key={lineNum} style={{ height:lineH, whiteSpace:"pre" }}>
      {tokens.map((t, i) => <span key={i} style={{ color:t.color }}>{t.text}</span>)}
    </div>
  ));

  if (readOnly) return (
    <div style={{ flex:1, display:"flex", overflow:"hidden", minHeight:0, position:"relative" }}>
      <div ref={gutterRef} style={gutterStyle}>
        {colorized.map(({ lineNum }) => <div key={lineNum} style={lineNumStyle}>{lineNum}</div>)}
      </div>
      <div ref={scrollRef} onScroll={syncScroll} style={{
        flex:1, overflow:"auto", padding:`${lineH*0.7}px 16px`,
        scrollbarWidth:"none", fontFamily:"monospace",
        fontSize, lineHeight:`${lineH}px`,
      }}>
        <Lines />
      </div>
      <GradientBar scrollEl={scrollRef} axis="v" />
      <GradientBar scrollEl={scrollRef} axis="h" />
    </div>
  );

  return (
    <div style={{ flex:1, display:"flex", overflow:"hidden", minHeight:0, position:"relative" }}>
      {/* gutter */}
      <div ref={gutterRef} style={gutterStyle}>
        {colorized.map(({ lineNum }) => <div key={lineNum} style={lineNumStyle}>{lineNum}</div>)}
      </div>
      {/* colored mirror */}
      <div ref={mirrorRef} aria-hidden style={{
        position:"absolute", top:0, left:gutterW, right:0, bottom:0,
        padding:`${lineH*0.7}px 16px`,
        fontFamily:"monospace", fontSize, lineHeight:`${lineH}px`,
        whiteSpace:"pre", pointerEvents:"none", overflow:"hidden",
      }}>
        <Lines />
      </div>
      {/* editable textarea */}
      <textarea
        ref={(el) => { scrollRef.current = el; if (taRef) taRef.current = el; }}
        value={text}
        onChange={e => {
          const ta = e.target;
          const ss = ta.selectionStart, se = ta.selectionEnd;
          onChange(ta.value);
          requestAnimationFrame(() => {
            if (ta === document.activeElement) ta.setSelectionRange(ss, se);
          });
        }}
        onScroll={syncScroll}
        spellCheck={false}
        style={{
          position:"absolute", top:0, left:gutterW, right:0, bottom:0,
          resize:"none", background:"transparent", border:"none", outline:"none",
          padding:`${lineH*0.7}px 16px`,
          fontFamily:"monospace", fontSize, lineHeight:`${lineH}px`,
          color:"transparent", caretColor:"rgba(255,255,255,0.8)",
          whiteSpace:"pre",
          overflowY:"auto", overflowX:"auto",
          scrollbarWidth:"none", zIndex:2,
        }}
      />
      <GradientBar scrollEl={scrollRef} axis="v" />
      <GradientBar scrollEl={scrollRef} axis="h" />
    </div>
  );
};

// ── UI helpers ─────────────────────────────────────────────────────────────────
const tabBtnSx = (active) => ({
  fontFamily:"'Barlow',sans-serif", fontSize:13, fontWeight: active ? 600 : 400,
  letterSpacing:"0.06em", textTransform:"none", minWidth:0,
  px:2, py:0.6, borderRadius:0,
  borderBottom: active ? "2px solid #10b981" : "2px solid transparent",
  color: active ? "#10b981" : "rgba(255,255,255,0.35)",
  "&:hover":{ color:"rgba(255,255,255,0.75)", borderColor:"transparent" },
});

const IdBtn = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    fontFamily:"'Barlow',sans-serif", fontSize:12, fontWeight: active ? 600 : 400,
    letterSpacing:"0.07em", textTransform:"uppercase", padding:"4px 14px",
    border:`1px solid ${active ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.15)"}`,
    background: active ? "rgba(255,255,255,0.08)" : "transparent",
    color: active ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.35)",
    cursor:"pointer", transition:"all 0.2s ease", outline:"none",
  }}>{label}</button>
);

const NavBtn = ({ onClick, children }) => (
  <button onClick={onClick} style={{
    background:"transparent", border:"1px solid rgba(255,255,255,0.12)",
    color:"rgba(255,255,255,0.45)", cursor:"pointer", outline:"none",
    display:"flex", alignItems:"center", justifyContent:"center",
    width:24, height:24, padding:0, transition:"all 0.2s",
  }}
    onMouseEnter={e => e.currentTarget.style.color = "rgba(255,255,255,0.85)"}
    onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.45)"}
  >{children}</button>
);

const FsBtn = ({ onClick, disabled }) => (null); // placeholder — defined inline below

// ── constants ──────────────────────────────────────────────────────────────────
const TOP_H   = 48;
const FOOTER_H = 34;
const FS_MIN  = 9;
const FS_MAX  = 18;
const FS_STEP = 0.5;
const HIST_LIMIT = 100;
const DEBOUNCE_MS = 2000; // 2s after last keystroke before validate+parse

// ── PAGE ───────────────────────────────────────────────────────────────────────
const SceneBuilder = () => {
  const initialJson = JSON.stringify(SAMPLE, null, 2);

  // ── core state ────────────────────────────────────────────────────────────
  const [jsonText,     setJsonText]     = useState(initialJson);
  const [parsed,       setParsed]       = useState(SAMPLE);
  const [renderError,  setRenderError]  = useState(null);
  const [activeIdx,    setActiveIdx]    = useState(null);
  const [view,         setView]         = useState("split");
  const [copied,       setCopied]       = useState(false);
  const [syntaxResult, setSyntaxResult] = useState({ valid:true, error:null });
  const [jsonFontSize, setJsonFontSize] = useState(12.5);

  // ── refs ──────────────────────────────────────────────────────────────────
  const taRef           = useRef(null);
  const noteRef         = useRef(null);
  const validateTimer   = useRef(null);

  // editorRaw: always holds the latest raw text visible in the editor,
  // even during the 2s debounce window before state catches up.
  // Render button reads from here so it's always current.
  const editorRawRef    = useRef(initialJson);

  // activeIdxRef mirrors activeIdx so callbacks that close over it
  // don't go stale between renders.
  const activeIdxRef    = useRef(null);
  useEffect(() => { activeIdxRef.current = activeIdx; }, [activeIdx]);

  // ── undo / redo ───────────────────────────────────────────────────────────
  const historyRef  = useRef([initialJson]);
  const histIdxRef  = useRef(0);
  const skipHistRef = useRef(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const syncHistFlags = useCallback(() => {
    setCanUndo(histIdxRef.current > 0);
    setCanRedo(histIdxRef.current < historyRef.current.length - 1);
  }, []);

  const pushHistory = useCallback((val) => {
    if (skipHistRef.current) return;
    const hist = historyRef.current;
    // drop any redo tail
    hist.splice(histIdxRef.current + 1);
    hist.push(val);
    if (hist.length > HIST_LIMIT) hist.shift();
    histIdxRef.current = hist.length - 1;
    syncHistFlags();
  }, [syncHistFlags]);

  // applyHistVal: used by undo/redo — bypasses debounce, applies immediately
  const applyHistVal = useCallback((val) => {
    skipHistRef.current = true;
    clearTimeout(validateTimer.current);

    editorRawRef.current = val;
    const result = validateJson(val);
    setSyntaxResult(result);
    setJsonText(val);
    setRenderError(null);

    if (result.valid) {
      try {
        const data = JSON.parse(val);
        setParsed(Array.isArray(data) ? data : [data]);
        setActiveIdx(null); // undo/redo always resets to full view
      } catch { /* ignore */ }
    }

    skipHistRef.current = false;
    syncHistFlags();
  }, [syncHistFlags]);

  const handleUndo = useCallback(() => {
    if (histIdxRef.current <= 0) return;
    histIdxRef.current--;
    applyHistVal(historyRef.current[histIdxRef.current]);
  }, [applyHistVal]);

  const handleRedo = useCallback(() => {
    if (histIdxRef.current >= historyRef.current.length - 1) return;
    histIdxRef.current++;
    applyHistVal(historyRef.current[histIdxRef.current]);
  }, [applyHistVal]);

  useEffect(() => {
    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); handleUndo(); }
      if ((e.key === 'z' && e.shiftKey) || e.key === 'y') { e.preventDefault(); handleRedo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleUndo, handleRedo]);

  // ── handleJsonChange: called on every keystroke ───────────────────────────
  // Only updates the visible text immediately.
  // Validation + parse is debounced to 2s after last keystroke.
  const handleJsonChange = useCallback((val) => {
    // 1. Track raw value immediately — render button always reads this
    editorRawRef.current = val;

    // 2. Update editor display immediately (no lag)
    setJsonText(val);

    // 3. Push to undo history
    if (!skipHistRef.current) pushHistory(val);

    // 4. Clear any pending debounced work
    clearTimeout(validateTimer.current);

    // 5. After 2s silence: validate + attempt parse
    validateTimer.current = setTimeout(() => {
      const result = validateJson(val);
      setSyntaxResult(result);
      setRenderError(null);

      if (!result.valid) return; // leave parsed state untouched if broken

      try {
        const data = JSON.parse(val);
        const idx  = activeIdxRef.current; // read ref, not stale closure

        if (idx !== null) {
          // editing single entry — merge back into full array
          setParsed(prev => {
            const next = [...prev];
            next[idx] = Array.isArray(data) ? data[0] : data;
            return next;
          });
        } else {
          setParsed(Array.isArray(data) ? data : [data]);
        }
      } catch { /* still not valid despite passing validateJson — leave parsed alone */ }
    }, DEBOUNCE_MS);

  }, [pushHistory]); // no `parsed`, no `activeIdx` — use refs instead

  // cleanup debounce timer on unmount
  useEffect(() => () => clearTimeout(validateTimer.current), []);

  // ── handleRender: render button ───────────────────────────────────────────
  // Reads editorRawRef.current — always the latest text, even mid-debounce.
  // This is the ONLY place where a parse failure updates renderError.
  const handleRender = useCallback(() => {
    // Cancel any pending debounce — render takes over right now
    clearTimeout(validateTimer.current);
    setRenderError(null);

    const source = editorRawRef.current;
    const result = validateJson(source);
    setSyntaxResult(result);

    if (!result.valid) {
      setRenderError(result.error);
      return;
    }

    try {
      const data = JSON.parse(source);
      const idx  = activeIdxRef.current;

      if (idx !== null) {
        // editing single entry: merge into full array, then deselect
        setParsed(prev => {
          const next = [...prev];
          next[idx] = Array.isArray(data) ? data[0] : data;
          // also update the canonical jsonText to reflect the merge
          const merged = JSON.stringify(next, null, 2);
          editorRawRef.current = merged;
          setJsonText(merged);
          return next;
        });
        setActiveIdx(null);
      } else {
        const newParsed = Array.isArray(data) ? data : [data];
        // re-serialize to canonical pretty form
        const canonical = JSON.stringify(newParsed, null, 2);
        editorRawRef.current = canonical;
        setJsonText(canonical);
        setParsed(newParsed);
      }

      setSyntaxResult({ valid:true, error:null });
    } catch(e) {
      setRenderError(e.message);
    }
  }, []); // stable — reads only refs and calls setState

  // ── handleTextClick: click scene text → jump to it in JSON editor ─────────
  const handleTextClick = useCallback((text, charOffset) => {
    const currentIdx = activeIdxRef.current;

    // find which entry owns this text
    let entryIdx = currentIdx;
    if (currentIdx === null) {
      setParsed(prev => {
        entryIdx = prev.findIndex(e => {
          const s = e?.prompt?.scenario?.scene || e?.prompt?.scene || [];
          return s.some(l => l.text === text || l.speaker === text);
        });
        if (entryIdx === -1) entryIdx = 0;
        return prev; // no change
      });
    }

    if (entryIdx === null || entryIdx === -1) entryIdx = 0;
    setActiveIdx(entryIdx);

    // place cursor in JSON after next render
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const ta = taRef.current;
      if (!ta) return;

      // editorJson for this entry
      setParsed(prev => {
        const json   = JSON.stringify(prev[entryIdx] ?? prev[0], null, 2);
        const target = JSON.stringify(text);
        const jsonIdx = json.indexOf(target);
        if (jsonIdx === -1) return prev;

        const cursorPos = jsonIdx + 1 + Math.min(charOffset, text.length);
        ta.focus();
        ta.setSelectionRange(cursorPos, cursorPos);

        const lines  = json.slice(0, cursorPos).split("\n");
        const lineH  = 12.5 * 1.7;
        ta.scrollTop = Math.max(0, (lines.length - 1) * lineH - ta.clientHeight / 2 + lineH);

        const col   = lines[lines.length-1].length;
        const colPx = col * 12.5 * 0.6;
        if (colPx + 16 > ta.scrollLeft + ta.clientWidth - 20)
          ta.scrollLeft = colPx + 16 - ta.clientWidth / 2;

        return prev;
      });
    }));
  }, []);

  // ── copy ──────────────────────────────────────────────────────────────────
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(editorRawRef.current).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }, []);

  // ── navigation ────────────────────────────────────────────────────────────
  const handleIdClick = useCallback((i) => setActiveIdx(p => p === i ? null : i), []);
  const handlePrev    = useCallback(() => setActiveIdx(p => {
    setParsed(prev => { const len = prev.length; setActiveIdx(p === null ? len-1 : (p-1+len)%len); return prev; });
    return p; // won't actually be used
  }), []);
  const handleNext    = useCallback(() => {
    setParsed(prev => { const len = prev.length; setActiveIdx(p => p === null ? 0 : (p+1)%len); return prev; });
  }, []);

  const fsUp   = useCallback(() => setJsonFontSize(f => Math.min(FS_MAX, +(f+FS_STEP).toFixed(1))), []);
  const fsDown = useCallback(() => setJsonFontSize(f => Math.max(FS_MIN, +(f-FS_STEP).toFixed(1))), []);

  // ── derived display values (memoized) ─────────────────────────────────────
  const displayEntries = useMemo(() =>
    activeIdx === null ? parsed : [parsed[activeIdx]],
    [parsed, activeIdx]
  );

  const totalLines = useMemo(() =>
    displayEntries.reduce((s, e) => s + (e?.prompt?.scenario?.scene || e?.prompt?.scene || []).length, 0),
    [displayEntries]
  );

  // The JSON text shown in the editor:
  // - all entries when nothing selected
  // - single entry (unwrapped from array) when one is selected
  const editorDisplayText = useMemo(() =>
    activeIdx === null ? jsonText : JSON.stringify(parsed[activeIdx], null, 2),
    [jsonText, parsed, activeIdx]
  );
  
  const contextNoteData = {
    title:    "Scene Builder — Kaggle Competition Tool",
    subtitle: "Vision Tools · Internal utility · Not production",
    lamp:     "#10b981",
    brand:    "Vision Tools · Scene Builder",
    storageKey: "sceneBuilderNoteEnabled",   // ← add this
    tags: [
      { label: "Kaggle",                     color: "purple" },
      { label: "Social Cognition Benchmark",  color: "blue"   },
      { label: "UI / Data Pipeline",          color: "green"  },
    ],
    sections: [
      {
        label: "What is this?",
        body: `This tool was built for a <strong style="color:rgba(255,255,255,0.88)">Kaggle competition</strong>
              focused on benchmarking <strong style="color:rgba(255,255,255,0.88)">social cognition</strong>
              across five language models — sarcasm detection, false assumptions,
              register mismatch, and similar pragmatic constructs. It shows bit of design.`,
      },
      {
        label: "Why it exists",
        body: `The underlying dataset uses JSON. The Scene Builder was a
              <strong style="color:rgba(255,255,255,0.88)">quick internal helper</strong>
              to speed up authoring, improve readability during labeling, and
              validate structure before submission.
              <br/><br/>
              <strong style="color:rgba(255,255,255,0.88)">No trading or financial data involved.</strong>`,
      },
    ],
  };

  useEffect(() => {
    const disabled = localStorage.getItem("sceneBuilderNoteEnabled") === "false";
    if (!disabled) {
      setTimeout(() => noteRef.current?.handleOpen(), 600);
    }
  }, []);

  const isValid  = syntaxResult.valid;
  const errorH   = renderError ? 32 : 0;
  const midTop   = TOP_H + errorH;
  const midH     = `calc(100% - ${TOP_H + errorH + FOOTER_H}px)`;

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ position:"relative", width:"100%", height:"100%", overflow:"hidden" }}>

      {/* ── TOP BAR ── */}
      <motion.div variants={fadeIn} initial="hidden" animate="visible" style={{
        position:"absolute", top:0, left:0, right:0, height:TOP_H,
        display:"flex", alignItems:"center", gap:6, padding:"0 20px",
        borderBottom:"1px solid rgba(255,255,255,0.07)", zIndex:10,
      }}>
        {["split","editor","scene"].map(v => (
          <Button key={v} onClick={() => setView(v)} sx={tabBtnSx(view === v)}>{v}</Button>
        ))}

        {parsed.length > 1 && (
          <>
            <div style={{ width:1, height:18, background:"rgba(255,255,255,0.1)", margin:"0 6px" }}/>
            <NavBtn onClick={() => setActiveIdx(p => p === null ? parsed.length-1 : (p-1+parsed.length)%parsed.length)}>
              <NavigateBeforeIcon sx={{ fontSize:16 }}/>
            </NavBtn>
            {parsed.map((entry, i) => (
              <IdBtn key={i} label={`#${entry.id ?? i+1}`} active={activeIdx === i} onClick={() => handleIdClick(i)} />
            ))}
            <NavBtn onClick={() => setActiveIdx(p => p === null ? 0 : (p+1)%parsed.length)}>
              <NavigateNextIcon sx={{ fontSize:16 }}/>
            </NavBtn>
          </>
        )}

        <div style={{ width:1, height:18, background:"rgba(255,255,255,0.1)", margin:"0 6px" }}/>

        <a
        href="#"
        onClick={(e) => { e.preventDefault(); noteRef.current?.handleOpen(); }}
        style={{
          display: "flex", alignItems: "center", gap: 4,
          fontSize: 13, fontFamily: "'Barlow', sans-serif",
          fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase",
          color: "rgba(255,255,255,0.48)", textDecoration: "none",
          transition: "color 0.2s",
        }}
        onMouseEnter={e => e.currentTarget.style.color = "rgba(255,255,255,0.75)"}
        onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.48)"}
        >
        About · Help
        <HelpOutlineIcon sx={{ fontSize: 14 }} />
      </a>

      <div style={{ flex:1 }}/>

        {activeIdx !== null && (parsed[activeIdx]?.prompt?.domain_tags || []).map(tag => (
          <span key={tag} style={{
            fontSize:12, padding:"4px 11px",
            background:"rgba(59,130,246,0.1)", border:"1px solid rgba(59,130,246,0.3)",
            color:"#60a5fa", letterSpacing:"0.06em", fontWeight:600, fontFamily:"'Barlow',sans-serif", marginLeft:4,
          }}>{tag}</span>
        ))}

        <Tooltip title={copied ? "Copied!" : "Copy JSON"}>
          <IconButton onClick={handleCopy} size="small" sx={{ color:"rgba(255,255,255,0.3)", ml:1, "&:hover":{ color:"#10b981" } }}>
            {copied ? <CheckIcon sx={{ fontSize:18 }}/> : <ContentCopyIcon sx={{ fontSize:18 }}/>}
          </IconButton>
        </Tooltip>

        <Button
          variant="outlined"
          startIcon={<PlayArrowIcon sx={{ fontSize:16 }}/>}
          onClick={handleRender}
          sx={{
            fontFamily:"'Barlow',sans-serif", fontSize:13, fontWeight:600,
            letterSpacing:"0.06em", textTransform:"none", px:2, py:0.6,
            borderColor:"rgba(156,163,175,0.5)", color:"rgba(255,255,255,0.78)", borderRadius:0,
            transition:"all 0.3s ease",
            "&:hover":{ borderColor:"transparent", borderImage:"linear-gradient(to right,#8018e7,#10b981,#3b82f6) 1", color:"#fff" },
          }}
        >Render</Button>
      </motion.div>

      {/* ── ERROR BAR ── */}
      <AnimatePresence>
        {renderError && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} style={{
            position:"absolute", top:TOP_H, left:0, right:0, height:32,
            padding:"0 20px", display:"flex", alignItems:"center",
            background:"rgba(239,68,68,0.1)", borderBottom:"1px solid rgba(239,68,68,0.3)",
            fontSize:12, color:"#ef4444", fontFamily:"monospace", zIndex:10,
          }}>{renderError}</motion.div>
        )}
      </AnimatePresence>

      {/* ── MIDDLE ── */}
      <div style={{ position:"absolute", top:midTop, left:0, right:0, height:midH, display:"flex", overflow:"hidden" }}>

        {/* Editor panel */}
        {(view === "split" || view === "editor") && (
          <motion.div variants={leftEntry} initial="hidden" animate="visible" style={{
            width: view === "split" ? "40%" : "100%",
            flexShrink:0, display:"flex", flexDirection:"column",
            borderRight: view === "split" ? "1px solid rgba(255,255,255,0.07)" : "none",
            height:"100%", overflow:"hidden",
          }}>
            {/* Editor header */}
            <div style={{
              padding:"4px 16px", fontSize:10, letterSpacing:"0.09em", textTransform:"uppercase",
              color:"rgba(255,255,255,0.18)", fontFamily:"'Barlow',sans-serif", fontWeight:500,
              borderBottom:"1px solid rgba(255,255,255,0.05)", flexShrink:0,
              display:"flex", alignItems:"center", gap:8,
            }}>
              <span>JSON input</span>

              {/* font size controls */}
              <div style={{ display:"flex", alignItems:"center", gap:4, marginLeft:4 }}>
                <button onClick={fsDown} style={{
                  background:"transparent", border:"1px solid rgba(255,255,255,0.1)",
                  color:"rgba(255,255,255,0.4)", cursor:"pointer", outline:"none",
                  width:20, height:20, display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:14, fontFamily:"monospace", lineHeight:1, padding:0,
                }}>−</button>
                <span style={{ fontSize:10, color:"rgba(255,255,255,0.3)", minWidth:28, textAlign:"center", fontFamily:"monospace" }}>
                  {jsonFontSize}
                </span>
                <button onClick={fsUp} style={{
                  background:"transparent", border:"1px solid rgba(255,255,255,0.1)",
                  color:"rgba(255,255,255,0.4)", cursor:"pointer", outline:"none",
                  width:20, height:20, display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:14, fontFamily:"monospace", lineHeight:1, padding:0,
                }}>+</button>
              </div>

              {/* undo / redo */}
              <div style={{ display:"flex", alignItems:"center", gap:3, marginLeft:4 }}>
                <Tooltip title="Undo (Ctrl+Z)">
                  <span>
                    <button
                      onClick={handleUndo}
                      disabled={!canUndo}
                      style={{
                        background:"transparent", border:"1px solid rgba(255,255,255,0.1)",
                        color: canUndo ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.15)",
                        cursor: canUndo ? "pointer" : "default", outline:"none",
                        width:20, height:20, display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize:14, fontFamily:"monospace", lineHeight:1, padding:0,
                      }}
                    >↩</button>
                  </span>
                </Tooltip>
                <Tooltip title="Redo (Ctrl+Shift+Z)">
                  <span>
                    <button
                      onClick={handleRedo}
                      disabled={!canRedo}
                      style={{
                        background:"transparent", border:"1px solid rgba(255,255,255,0.1)",
                        color: canRedo ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.15)",
                        cursor: canRedo ? "pointer" : "default", outline:"none",
                        width:20, height:20, display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize:14, fontFamily:"monospace", lineHeight:1, padding:0,
                      }}
                    >↪</button>
                  </span>
                </Tooltip>
              </div>

              <div style={{ flex:1 }}/>

              {activeIdx !== null && (
                <span style={{ color:"rgba(255,255,255,0.28)", fontSize:10 }}>
                  editing #{parsed[activeIdx]?.id ?? activeIdx+1}
                </span>
              )}
            </div>

            <JsonViewer
              text={editorDisplayText}
              readOnly={false}
              onChange={handleJsonChange}
              fontSize={jsonFontSize}
              taRef={taRef}
            />
          </motion.div>
        )}

        {/* Scene panel */}
        {(view === "split" || view === "scene") && (
          <motion.div variants={rightEntry} initial="hidden" animate="visible" style={{
            flex:1, height:"100%", overflowY:"auto", padding:"16px 20px",
            scrollbarWidth:"none", minWidth:0,
          }}>
            {displayEntries.length === 0 && (
              <div style={{ color:"rgba(255,255,255,0.18)", fontSize:15, fontFamily:"'Barlow',sans-serif", textAlign:"center", marginTop:60 }}>
                Paste JSON and click Render
              </div>
            )}
            {displayEntries.map((entry, i) => (
              <SceneBlock
                key={activeIdx !== null ? activeIdx : i}
                entry={entry}
                offset={i * 100}
                onTextClick={handleTextClick}
              />
            ))}
          </motion.div>
        )}
      </div>

      {/* ── FOOTER ── */}
      <div style={{
        position:"absolute", bottom:0, left:0, right:0, height:FOOTER_H,
        display:"flex", alignItems:"center", padding:"0 20px", gap:20,
        borderTop:"1px solid rgba(255,255,255,0.06)",
        background:"rgba(0,0,0,0.18)", zIndex:10,
      }}>
        {/* Status lamp */}
        <div style={{ display:"flex", alignItems:"center", gap:7 }}>
          <span style={{
            width:7, height:7, borderRadius:"50%", display:"inline-block", flexShrink:0,
            background: isValid ? "#10b981" : "#a855f7",
            boxShadow: isValid ? "0 0 7px #10b981" : "0 0 7px #a855f7",
            transition:"background 0.3s, box-shadow 0.3s",
          }}/>
          <span style={{
            fontSize:11, fontFamily:"'Barlow',sans-serif", letterSpacing:"0.08em", textTransform:"uppercase",
            color: isValid ? "rgba(255,255,255,0.35)" : "#a855f7", transition:"color 0.3s",
          }}>
            {isValid ? "ready" : "invalid"}
          </span>
        </div>

        <div style={{ width:1, height:14, background:"rgba(255,255,255,0.07)" }}/>

        {!isValid && syntaxResult.error ? (
          <span style={{ fontSize:11, fontFamily:"monospace", color:"#c084fc", letterSpacing:"0.03em" }}>
            {syntaxResult.error}
          </span>
        ) : (
          <>
            <span style={{ fontSize:11, fontFamily:"'Barlow',sans-serif", letterSpacing:"0.07em", color:"rgba(255,255,255,0.35)", textTransform:"uppercase" }}>
              scenes <span style={{ color:"rgba(255,255,255,0.6)" }}>{displayEntries.length}</span>
            </span>
            <span style={{ fontSize:11, fontFamily:"'Barlow',sans-serif", letterSpacing:"0.07em", color:"rgba(255,255,255,0.35)", textTransform:"uppercase" }}>
              lines <span style={{ color:"rgba(255,255,255,0.6)" }}>{totalLines}</span>
            </span>
          </>
        )}

        <div style={{ flex:1 }}/>
        <span
        onClick={() => noteRef.current?.handleOpen()}
        onMouseEnter={e => e.currentTarget.style.opacity = "1"}
        onMouseLeave={e => e.currentTarget.style.opacity = "0.65"}
        style={{
          fontSize:11, fontFamily:"'Barlow',sans-serif", letterSpacing:"0.1em", textTransform:"uppercase",
          background:"linear-gradient(to right,#8018e7,#10b981,#3b82f6)",
          WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
          opacity:0.65, cursor:"pointer", transition:"opacity 0.2s",
        }}
      >
        Vision Tools · Scene Builder
      </span>

      </div>
      <ContextNoteDialog ref={noteRef} data={contextNoteData} />


    </div>
  );
};

export default SceneBuilder;